import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Orbit, Share2, ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { AuthDialog } from "@/components/AuthDialog";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1760443267021-8df5fa55204e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const steps = [
  { Icon: Compass, code: "01", title: "Discover", text: "Launch a probe. ARCHIVE invents a world no one has ever seen: biome, sky, hazards, myth." },
  { Icon: Orbit, code: "02", title: "Explore", text: "Enter the dossier. Five decks of lore, environment, life and travel notes. Jump worlds instantly." },
  { Icon: Share2, code: "03", title: "Share", text: "Send a link. Friends react, leave log entries, and save your find to their own library." },
];

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const enter = () => (user ? navigate("/universe") : setAuthOpen(true));

  return (
    <div className="relative z-10 min-h-screen" data-testid="landing-page">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img src={HERO_IMG} alt="" className="h-[90vh] w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05070D]/30 via-[#05070D]/70 to-[#05070D]" />
      </div>
      <Header onSignIn={() => setAuthOpen(true)} />

      <main className="mx-auto max-w-7xl px-6 pt-20 pb-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="label-mono mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-cyan-400" /> Universe · v1 · Discovery network online
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-slate-50" data-testid="landing-headline">
              Chart worlds<br /><span className="text-cyan-300 text-glow">no one</span> has seen.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}
              className="mt-8 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              Every probe you launch returns a brand-new AI-generated planet: a painted orbital view, an atmosphere, hazards, native life and a myth worth retelling. Save the ones you love. Send the strange ones to friends.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="mt-10 flex flex-wrap gap-4">
              <button onClick={enter} className="btn-primary !px-8 !py-4" data-testid="enter-universe-button">
                Enter the universe <ArrowRight className="h-4 w-4" />
              </button>
              <a href="#how" className="btn-ghost !px-6 !py-4" data-testid="how-it-works-link">How it works</a>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="lg:col-span-5 relative flex justify-center">
            <div className="relative h-72 w-72 sm:h-96 sm:w-96 animate-float">
              <div className="absolute inset-[-18%] rounded-full border border-cyan-400/20 animate-orbit">
                <span className="absolute -top-1.5 left-1/2 h-3 w-3 rounded-full bg-cyan-300 shadow-[0_0_14px_#00F0FF]" />
              </div>
              <div className="absolute inset-[-34%] rounded-full border border-[#8A2BE2]/20 animate-orbit" style={{ animationDuration: "40s", animationDirection: "reverse" }}>
                <span className="absolute top-1/2 -right-1 h-2 w-2 rounded-full bg-fuchsia-300 shadow-[0_0_10px_#8A2BE2]" />
              </div>
              <div className="absolute inset-0 rounded-full"
                style={{ background: "radial-gradient(circle at 35% 30%, #FFC700 0%, #00F0FF 25%, #1e1b4b 60%, #05070D 100%)", boxShadow: "inset -40px -30px 90px rgba(0,0,0,0.85), 0 0 80px rgba(0,240,255,0.25)" }} />
              <div className="absolute inset-0 rounded-full opacity-30 mix-blend-overlay" style={{ background: "repeating-linear-gradient(165deg, rgba(255,255,255,0.15) 0 8px, transparent 8px 26px)" }} />
            </div>
          </motion.div>
        </div>

        <section id="how" className="mt-40 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.code} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass hud-corners rounded-xl p-8 card-hover" data-testid={`landing-step-${s.title.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-8">
                <s.Icon className="h-6 w-6 text-cyan-300" />
                <span className="font-mono text-xs text-slate-500">{s.code}</span>
              </div>
              <h3 className="font-display text-xl font-semibold tracking-wide text-slate-50 mb-3">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </section>
      </main>

      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
